import { useState } from "react";

/**
 * LockPanel — Task 4.10 / Req 20.
 *
 * Renders a locked paid-tier feature placeholder with upgrade CTA. Used by
 * ReportView to show free-tier users what's behind the paywall WITHOUT
 * exposing any paid data (Req 20.3 — Pattern C security model from Task
 * 4.4 + 4.8a: free-tier responses NEVER include paid data; LockPanels are
 * static text + an async refetch trigger on upgrade per Req 20.5a).
 *
 * Task 9.9a simplification: the panel header (lock icon + title + "Pro"
 * badge) IS the disclosure trigger that expands/collapses the description
 * box — the separate "See what's in this paid feature" text link is gone.
 * Click-to-reveal (not hover) since hover doesn't work on touch.
 */

export type LockedFeature =
  | "tilt_map"
  | "owner_equity"
  | "fair_value_comps"
  | "permits"
  | "flood_risk"
  | "saved_reports"
  | "cross_session_memory"
  | "alerts"
  | "multi_property"
  | "deep_data";

interface LockPanelProps {
  feature: LockedFeature;
  title: string;
  description: string;
  /** Per-feature emoji, mirroring the marketing LOCKED_FEATURES icons. */
  icon?: string;
  /** Where to send the user on upgrade — typically routes to Stripe Checkout. */
  onUpgradeClick: () => void;
}

export function LockPanel({ feature, title, description, icon = "🔒", onUpgradeClick }: LockPanelProps) {
  const [revealed, setRevealed] = useState(false);

  return (
    <section
      className={`lock-panel lock-panel-${feature}`}
      aria-label={`${title} (locked — PriceTilt Pro feature)`}
    >
      <button
        type="button"
        className="lock-panel-header"
        onClick={() => setRevealed((v) => !v)}
        aria-expanded={revealed}
      >
        <span className="lock-icon" aria-hidden>
          {icon}
        </span>
        <span className="lock-title">{title}</span>
        <span className="lock-badge">Pro</span>
        <span className="lock-chevron" aria-hidden>
          {revealed ? "▾" : "▸"}
        </span>
      </button>

      {revealed && (
        <div className="lock-panel-body">
          <p>{description}</p>
          <button
            type="button"
            className="upgrade-cta"
            onClick={onUpgradeClick}
          >
            Upgrade to PriceTilt Pro
          </button>
        </div>
      )}
    </section>
  );
}
